import React, { useMemo } from 'react'
import { usePrevious } from 'react-use'

import { useIsMutating } from '@tanstack/react-query'
import _ from 'lodash'
import { useShallow } from 'zustand/react/shallow'

import { Button } from '@harvey-frontend/components/ui/button'
import { Spinner } from '@harvey-frontend/components/ui/spinner'

import {
  MIN_HIGHLIGHT_CHARACTER_LENGTH,
  MAX_HIGHLIGHT_CHARACTER_LENGTH,
} from '../../../utils/constants'
import { RequestMode } from '../../../utils/constants'
import { useFetchEdits } from '../hooks/useFetchEdits'
import { useFetchInserts } from '../hooks/useFetchInserts'
import { useFetchSuggestions } from '../hooks/useFetchSuggestions'
import { MUTATION_KEY } from '../hooks/useFormatOoxml'
import { useRequestMode } from '../hooks/useRequestMode'
import { FileStatus } from '../stores/app-store'
import useAppStore from '../stores/store'

const AskHarveyButton = () => {
  const mode = useRequestMode()
  const {
    files,
    highlightedText,
    instruction,
    isApplyingSuggestion,
    isFetching,
    isDropzoneLoading,
    reviewSuggestions,
    queryCompleted,
    setIsFetching,
    resetServerState,
  } = useAppStore(
    useShallow((s) => ({
      files: s.files,
      highlightedText: s.highlightedText,
      instruction: s.instruction,
      isApplyingSuggestion: s.isApplyingSuggestion,
      isFetching: s.isFetching,
      isDropzoneLoading: s.isDropzoneLoading,
      reviewSuggestions: s.reviewSuggestions,
      queryCompleted: s.queryCompleted,
      queryError: s.queryError,
      resetServerState: s.resetServerState,
      setIsFetching: s.setIsFetching,
    }))
  )
  const isFormatting = useIsMutating({
    mutationKey: [MUTATION_KEY],
  })

  // Store the previous request state to check if the user has modified the highlighted text or instruction.
  const previousRequestState = usePrevious({
    highlightedText: highlightedText?.text,
    instruction,
    reviewSuggestions,
  })

  const filesToUse = files.flatMap((file) => {
    if (
      file.status === FileStatus.UPLOADED &&
      file.selected &&
      file.uploadedFile
    ) {
      return [file.uploadedFile]
    }
    return []
  })

  const fetchEdits = useFetchEdits(filesToUse)
  const fetchSuggestions = useFetchSuggestions(filesToUse)
  const fetchInserts = useFetchInserts(filesToUse)

  const handleRequest = async () => {
    // Reset server state when fetching.
    setIsFetching(true)
    resetServerState()
    switch (mode) {
      case RequestMode.REVISE:
        await fetchSuggestions()
        break
      case RequestMode.INSERT:
        await fetchInserts()
        break
      default:
        await fetchEdits()
    }
    setIsFetching(false)
  }

  const isInvalidCharacterLength = useMemo(() => {
    return highlightedText?.text
      ? highlightedText.text.length < MIN_HIGHLIGHT_CHARACTER_LENGTH ||
          highlightedText.text.length > MAX_HIGHLIGHT_CHARACTER_LENGTH
      : false
  }, [highlightedText])

  const isInvalidHighlightText =
    [RequestMode.EDIT, RequestMode.REVISE].includes(mode) &&
    (!highlightedText || highlightedText.text.length === 0)
  const isUploadingFiles = files.some(
    (file) => file.status === FileStatus.UPLOADING
  )

  const disabledReason = useMemo(() => {
    if (isInvalidHighlightText) {
      return 'Please select some text'
    } else if (isInvalidCharacterLength) {
      return `Please select text between ${MIN_HIGHLIGHT_CHARACTER_LENGTH} and ${MAX_HIGHLIGHT_CHARACTER_LENGTH} characters`
    } else if (!instruction || instruction.length === 0) {
      return 'Please enter an instruction'
    } else if (isUploadingFiles) {
      return 'Please wait for the precedent files to upload'
    } else if (isFetching) {
      return 'Please wait for the request to complete'
    } else if (
      mode !== RequestMode.INSERT &&
      queryCompleted &&
      _.isEqual(previousRequestState, {
        highlightedText: highlightedText?.text,
        instruction,
        reviewSuggestions,
      })
    ) {
      return 'Please modify the highlighted text or instruction'
    }
    return ''
  }, [
    highlightedText,
    isFetching,
    isInvalidHighlightText,
    isInvalidCharacterLength,
    instruction,
    mode,
    isUploadingFiles,
    previousRequestState,
    reviewSuggestions,
    queryCompleted,
  ])

  const isAskHarveyDisabled = useMemo(() => {
    return (
      disabledReason !== '' ||
      isApplyingSuggestion ||
      isFetching ||
      isUploadingFiles ||
      isDropzoneLoading ||
      isInvalidCharacterLength
    )
  }, [
    isApplyingSuggestion,
    disabledReason,
    isFetching,
    isDropzoneLoading,
    isInvalidCharacterLength,
    isUploadingFiles,
  ])

  const buttonText = useMemo(() => {
    if (isFormatting) {
      return <span className="mt-0.5 text-xs font-medium">Formatting…</span>
    } else if (isFetching) {
      switch (mode) {
        case RequestMode.REVISE:
          return (
            <span className="mt-0.5 text-xs font-medium">
              Loading suggestions…
            </span>
          )
        case RequestMode.INSERT:
          return <span className="mt-0.5 text-xs font-medium">Drafting…</span>
        case RequestMode.EDIT:
          return <span className="mt-0.5 text-xs font-medium">Editing…</span>
      }
    }
    return mode === RequestMode.INSERT ? 'Draft new text' : 'Ask Harvey'
  }, [isFetching, isFormatting, mode])

  return (
    <Button
      className="w-full shrink-0 px-4 py-1.5"
      disabled={isAskHarveyDisabled}
      tooltip={disabledReason}
      onClick={handleRequest}
    >
      <div className="flex items-center gap-2 text-sm font-medium">
        {isFetching || isFormatting ? <Spinner size="xxs" /> : null}
        {buttonText}
      </div>
    </Button>
  )
}

export default AskHarveyButton
